import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Subscription } from '../services/api';
import SubscriptionPreview from './SubscriptionPreview';

interface SubscriptionViewModalProps {
  visible: boolean;
  subscription: Subscription | null;
  onCancel: () => void;
}

const SubscriptionViewModal: React.FC<SubscriptionViewModalProps> = ({
  visible,
  subscription,
  onCancel,
}) => {
  if (!visible || !subscription) return null;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'TRIAL':
        return 'bg-blue-100 text-blue-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-800';
      case 'ONGOING':
        return 'bg-yellow-100 text-yellow-800';
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-900">Subscription Details</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Details */}
              <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">ID</label>
                <p className="text-sm text-gray-900 mt-1">{subscription.id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(subscription.status)}`}>
                    {subscription.status}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Trial</label>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    subscription.isTrial 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {subscription.isTrial ? 'Yes' : 'No'}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* User Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">User Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">User ID</label>
                <p className="text-sm text-gray-900 mt-1">{subscription.userId}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <p className="text-sm text-gray-900 mt-1">{subscription.user?.email || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Name</label>
                <p className="text-sm text-gray-900 mt-1">{subscription.user?.name || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Product Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Product Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Product ID</label>
                <p className="text-sm text-gray-900 mt-1">{subscription.productId}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Product Name</label>
                <p className="text-sm text-gray-900 mt-1">{subscription.productName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Product Slug</label>
                <p className="text-sm text-gray-900 mt-1">{subscription.productSlug}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Billing Cycle</label>
                <p className="text-sm text-gray-900 mt-1">{subscription.productBillingCycle}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Duration Billing</label>
                <p className="text-sm text-gray-900 mt-1">
                  {subscription.productDurationBilling} {subscription.productBillingCycle === 'MONTHLY' ? 'Month(s)' : 'Year(s)'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Product Base Price</label>
                <p className="text-sm text-gray-900 mt-1">
                  {subscription.product?.basePrice ? formatPrice(subscription.product.basePrice) : 'N/A'}
                </p>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-500">Description</label>
              <p className="text-sm text-gray-900 mt-1">{subscription.productDescription}</p>
            </div>
          </div>

          {/* Pricing Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Current Price</label>
                <p className="text-sm text-gray-900 mt-1 font-semibold">{formatPrice(subscription.currentPrice)}</p>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dates</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Started At</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(subscription.startedAt)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Ended At</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(subscription.endedAt)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Created At</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(subscription.createdAt)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Updated At</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(subscription.updatedAt)}</p>
              </div>
            </div>
          </div>

              </div>

              {/* Right Column - Preview */}
              <div className="lg:sticky lg:top-20">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Subscription Preview</h3>
                  <SubscriptionPreview subscription={subscription} />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end pt-6 mt-6 border-t border-gray-200">
              <button
                onClick={onCancel}
                className="px-4 py-2 bg-imola-primary text-white rounded-md hover:bg-imola-primary/80 focus:outline-none focus:ring-2 focus:ring-imola-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionViewModal;
